import { Button } from '@crowdex/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@crowdex/ui/components/popover';
import { POPUP_SURFACE } from '@crowdex/ui/lib/surfaces';
import { cn } from '@crowdex/ui/lib/utils';
import { LayoutGrid } from 'lucide-react';
import {
  type CSSProperties,
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import {
  THREAD_SUMMARY_INSET,
  THREAD_SUMMARY_WIDTH,
  threadContentShift,
  threadSummaryMode,
} from '@/lib/desktop-shell/thread-summary-layout';

/** The open conversation's summary, rendered where it shows (`visible`: on screen there). */
const ThreadSummaryContext = createContext<((visible: boolean) => ReactNode) | null>(null);

export const ThreadSummaryProvider = ThreadSummaryContext.Provider;

/** The open conversation's summary, for a phone's Summary page (mobile 36); null without one. */
export function useThreadSummaryRender(): ((visible: boolean) => ReactNode) | null {
  return useContext(ThreadSummaryContext);
}

/** The card the summary sits in, beside the transcript or in its popover. */
function ThreadSummaryCard({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        POPUP_SURFACE,
        'flex h-fit max-h-full min-h-0 w-(--thread-summary-width) flex-col overflow-hidden rounded-card bg-card pointer-coarse:w-full pointer-coarse:rounded-none pointer-coarse:border-0 pointer-coarse:shadow-none',
      )}
      // The picture-in-picture's home: the floating stack goes back here, and avoids it.
      data-pip-home-surface
      data-pip-obstacle
      style={{ '--thread-summary-width': `${THREAD_SUMMARY_WIDTH}px` } as CSSProperties}
    >
      {/* On a phone the summary is a sheet of its own (36), titled. */}
      <h2 className="hidden type-card-title pt-1 text-center pointer-coarse:block">Summary</h2>
      <div className="min-h-0 overflow-x-hidden overflow-y-auto p-4">{children}</div>
    </div>
  );
}

/**
 * Lays out the open conversation's summary from the width of its pane, like Codex: pinned in the
 * free gutter beside the transcript, beside a transcript moved aside, or (no room) only as a
 * popover from the header's Summary button (D5b). Returns how far to move the transcript.
 */
export function useThreadSummaryLayout(
  pane: RefObject<HTMLElement | null>,
  enabled: boolean,
): number {
  const [width, setWidth] = useState<number | null>(null);
  const pinned = useDesktopShellStore((state) => state.summaryPinned);
  const setSummaryMode = useDesktopShellStore((state) => state.setSummaryMode);
  const setSummaryPopoverOpen = useDesktopShellStore((state) => state.setSummaryPopoverOpen);
  useEffect(() => {
    const element = pane.current;
    if (!enabled || element === null) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    setWidth(element.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [enabled, pane]);
  const mode = enabled && width !== null ? threadSummaryMode(width) : null;
  useEffect(() => {
    setSummaryMode(mode);
    // The popover only exists while the summary has no room beside the transcript.
    if (mode !== 'overlay') setSummaryPopoverOpen(false);
  }, [mode, setSummaryMode, setSummaryPopoverOpen]);
  useEffect(() => () => setSummaryMode(null), [setSummaryMode]);
  return mode === null ? 0 : threadContentShift(mode, pinned);
}

/** The summary pinned at the right of the transcript, while there is room for it. */
export function PinnedThreadSummary() {
  const render = useContext(ThreadSummaryContext);
  const pinned = useDesktopShellStore((state) => state.summaryPinned);
  const mode = useDesktopShellStore((state) => state.summaryMode);
  const shown = pinned && mode !== null && mode !== 'overlay';
  if (render === null) return null;
  return (
    <div
      aria-hidden={!shown || undefined}
      className="pointer-events-none absolute top-3 right-0 bottom-3 z-20 flex items-start"
      data-testid="thread-summary"
      inert={!shown || undefined}
      style={{ paddingInlineEnd: THREAD_SUMMARY_INSET }}
    >
      <div
        className={cn(
          'flex max-h-full min-h-0 origin-top-right transition-[opacity,translate,scale] duration-300 ease-out motion-reduce:transition-none',
          shown ? 'pointer-events-auto opacity-100' : 'translate-x-full scale-80 opacity-0',
        )}
      >
        <ThreadSummaryCard>{render(shown)}</ThreadSummaryCard>
      </div>
    </div>
  );
}

/**
 * The header's Summary button: pins or unpins the summary beside the transcript, or, when it
 * has no room there, opens it as a popover.
 */
export function ThreadSummaryToggle() {
  const render = useContext(ThreadSummaryContext);
  const mode = useDesktopShellStore((state) => state.summaryMode);
  const pinned = useDesktopShellStore((state) => state.summaryPinned);
  const popoverOpen = useDesktopShellStore((state) => state.summaryPopoverOpen);
  const setPinned = useDesktopShellStore((state) => state.setSummaryPinned);
  const setPopoverOpen = useDesktopShellStore((state) => state.setSummaryPopoverOpen);
  if (render === null || mode === null) return null;
  if (mode !== 'overlay')
    return (
      <Button
        aria-label="Toggle pinned summary"
        aria-pressed={pinned}
        className="max-sm:size-10"
        onClick={() => setPinned((current) => !current)}
        size="icon-sm"
        title={pinned ? 'Hide summary' : 'Show summary'}
        variant={pinned ? 'secondary' : 'ghost'}
      >
        <LayoutGrid className="size-4" />
      </Button>
    );
  return (
    <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label="Toggle summary"
          aria-pressed={popoverOpen}
          className="max-sm:size-10"
          size="icon-sm"
          title="Summary"
          variant={popoverOpen ? 'secondary' : 'ghost'}
        >
          <LayoutGrid className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        aria-label="Summary"
        align="end"
        className="!w-auto flex !border-0 !bg-transparent !p-0 !shadow-none pointer-coarse:!bg-card"
        side="bottom"
        sideOffset={8}
        style={{
          maxHeight: 'min(var(--radix-popover-content-available-height), calc(100vh - 16px))',
        }}
      >
        <ThreadSummaryCard>{render(popoverOpen)}</ThreadSummaryCard>
      </PopoverContent>
    </Popover>
  );
}
